import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import EventCard from '@/components/EventCard';
import NewsletterForm from '@/components/NewsletterForm';
import { CardSkeleton } from '@/components/Loading';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Search, MapPin, CalendarIcon, X, ArrowRight, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { it as itLocale, enGB } from 'date-fns/locale';
import { zoneSlug } from '@/lib/seo';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [zones, setZones] = useState([]);
  const [locali, setLocali] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ day: null, zona: 'all', locale_id: 'all' });
  const dateLocale = i18n.language?.startsWith('en') ? enGB : itLocale;

  useEffect(() => {
    Promise.all([
      api.get('/public/zones'),
      api.get('/public/locali'),
      api.get('/public/eventi', { params: { in_evidenza: true } }),
      api.get('/public/eventi', { params: { data_from: new Date().toISOString().slice(0, 10) } }),
    ]).then(([z, l, f, all]) => {
      setZones(z.data?.zones || []);
      setLocali(l.data || []);
      setFeatured((f.data || []).slice(0, 8));
      setAllEvents(all.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const localeMap = useMemo(() => Object.fromEntries(locali.map(l => [l.id, l])), [locali]);

  const filteredEvents = useMemo(() => {
    return allEvents.filter((e) => {
      if (filters.zona && filters.zona !== 'all' && e.zona !== filters.zona) return false;
      if (filters.locale_id && filters.locale_id !== 'all' && e.locale_id !== filters.locale_id) return false;
      if (filters.day) {
        const ds = format(filters.day, 'yyyy-MM-dd');
        if (e.data !== ds) return false;
      }
      return true;
    });
  }, [allEvents, filters]);

  const resetFilters = () => setFilters({ day: null, zona: 'all', locale_id: 'all' });

  return (
    <PublicLayout>
      <SEO
        title="Roma Nightlife — Eventi e Serate a Roma"
        description="Scopri le migliori serate, club ed eventi a Roma. Testaccio, EUR, Centro, Ponte Milvio, Ostia, Fregene e tutte le zone della Capitale."
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
      />

      {/* HERO */}
      <section className="relative overflow-hidden hero-overlay noise-bg" data-testid="home-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20 relative z-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">Roma • Nightlife • Premium</p>
          <h1 data-testid="home-hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[color:var(--text)] max-w-3xl leading-[1.05]">
            {t('hero.title')}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[color:var(--text-muted)] max-w-2xl">
            {t('hero.subtitle')}
          </p>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-2 md:-mt-4 relative z-20">
        <div className="rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-3 md:p-4 shadow-[0_18px_60px_rgba(0,0,0,0.6)]">
          <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-subtle)] mb-3">{t('hero.searchTitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1.2fr_1.6fr_auto_auto] gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="home-filter-day" className="justify-start font-normal bg-[color:var(--surface-2)] border-[color:var(--border-c)] text-[color:var(--text)] hover:bg-[rgba(214,185,140,0.06)]">
                  <CalendarIcon className="w-4 h-4 mr-2 text-[color:var(--gold)]" />
                  {filters.day ? format(filters.day, 'EEE d MMM', { locale: dateLocale }) : t('filters.pickDay')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[color:var(--surface)] border-[color:var(--border-c)]" align="start">
                <Calendar mode="single" selected={filters.day} onSelect={(d) => setFilters((f) => ({ ...f, day: d }))} locale={dateLocale} />
                <div className="p-2 border-t border-[color:var(--border-c)]">
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setFilters((f) => ({ ...f, day: null }))}>{t('filters.anyDay')}</Button>
                </div>
              </PopoverContent>
            </Popover>

            <Select value={filters.zona} onValueChange={(v) => setFilters((f) => ({ ...f, zona: v }))}>
              <SelectTrigger data-testid="home-filter-zone" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]">
                <MapPin className="w-4 h-4 mr-2 text-[color:var(--gold)]" />
                <SelectValue placeholder={t('filters.zone')} />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)]">
                <SelectItem value="all">{t('filters.anyZone')}</SelectItem>
                {zones.map((z) => (<SelectItem key={z} value={z}>{z}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={filters.locale_id} onValueChange={(v) => setFilters((f) => ({ ...f, locale_id: v }))}>
              <SelectTrigger data-testid="home-filter-venue" className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]">
                <SelectValue placeholder={t('filters.venue')} />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border-c)] max-h-[300px]">
                <SelectItem value="all">{t('filters.anyVenue')}</SelectItem>
                {locali.map((l) => (<SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>))}
              </SelectContent>
            </Select>

            <Button data-testid="home-search-submit-button" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold btn-press">
              <Search className="w-4 h-4 mr-1.5" /> {t('filters.search')}
            </Button>
            <Button data-testid="home-search-reset-button" variant="ghost" onClick={resetFilters} className="text-[color:var(--text-muted)] hover:text-[color:var(--gold)]">
              <X className="w-4 h-4 mr-1" /> {t('filters.reset')}
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURED CAROUSEL */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-12" data-testid="home-featured-section">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-heading text-2xl md:text-3xl">{t('sections.featured')}</h2>
            <span className="text-xs uppercase tracking-[0.2em] text-[color:var(--gold)]">Top picks</span>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar scroll-snap-x pb-2 -mx-4 px-4">
            {featured.map((e) => (
              <div key={e.id} className="snap-start min-w-[88%] sm:min-w-[60%] md:min-w-[48%] lg:min-w-[40%]">
                <EventCard evento={e} locale={localeMap[e.locale_id]} large />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* UPCOMING LIST */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-12">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading text-2xl md:text-3xl">{t('sections.upcoming')}</h2>
          {filteredEvents.length > 0 && <span className="text-xs text-[color:var(--text-muted)]">{filteredEvents.length} eventi</span>}
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)]" data-testid="home-empty-state">
            <p className="text-[color:var(--text-muted)]">{t('empty.events')}</p>
            <Button variant="outline" onClick={resetFilters} className="mt-4 border-[color:var(--gold)] text-[color:var(--gold)]">{t('empty.eventsCta')}</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredEvents.map((e) => (
              <EventCard key={e.id} evento={e} locale={localeMap[e.locale_id]} />
            ))}
          </div>
        )}
      </section>

      {/* NEWSLETTER MIDDLE BLOCK */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16" data-testid="home-newsletter-block">
        <div className="relative rounded-3xl border border-[rgba(214,185,140,0.3)] bg-[color:var(--surface)] p-6 md:p-10 overflow-hidden noise-bg">
          <div className="absolute inset-0 hero-overlay pointer-events-none"/>
          <div className="relative z-10 grid md:grid-cols-[1.1fr_1fr] gap-6 items-center">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)] mb-2 flex items-center gap-2"><Mail className="w-4 h-4"/>Newsletter</p>
              <h2 className="font-heading text-3xl md:text-4xl mb-2">{t('newsletter.bigTitle')}</h2>
              <p className="text-[color:var(--text-muted)] max-w-xl">{t('newsletter.text')}</p>
            </div>
            <div className="w-full">
              <NewsletterForm source="home" variant="inline" />
              <p className="text-xs text-[color:var(--text-subtle)] mt-3">Ricevi solo eventi selezionati. Niente spam. Disiscrivi quando vuoi.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ZONES */}
      {zones.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
          <h2 className="font-heading text-2xl md:text-3xl mb-4">{t('sections.zones')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {zones.map((z) => (
              <Link key={z} to={`/serate-roma-${zoneSlug(z)}`} className="group rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-4 hover:border-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.04)] transition-colors flex items-center justify-between">
                <span className="font-heading text-lg">{z}</span>
                <ArrowRight className="w-4 h-4 text-[color:var(--text-muted)] group-hover:text-[color:var(--gold)] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
